import { Link } from '@tanstack/react-router'
import type { LocationSearchResult } from '@/api/accommodation/types'
import Discount from '@/components/money/discount'
import Price from '@/components/money/price'
import { ImagePlaceholder } from '@/components/ui/ImagePlaceholder'
import { trackOfferClicked } from '@/lib/track'
import { unitFullName, unitPhotos } from '@/lib/unit'
import { WatchHeartButton } from '@/features/accommodation/watch-heart/WatchHeartButton'

/**
 * Compact square offer tile used inside the `::nabidky` block in articles.
 * Lighter than the search-page `SearchResultCard` (square, fewer details) so a
 * row of them fits the narrower article column.
 */
export function ArticleOfferCard({ offer, position }: { offer: LocationSearchResult; position?: number }) {
  const photos = unitPhotos(offer.unit, offer.location)
  const image = photos.length > 0 ? [...photos].sort((a, b) => b.priority - a.priority)[0] : null

  return (
    <Link
      to="/ubytovani/$slug/$unitSlug"
      params={{ slug: offer.location.slug, unitSlug: offer.unit.slug }}
      onClick={() => trackOfferClicked(offer, position, 'article')}
      className="group relative block aspect-square overflow-clip rounded-xl transition-opacity hover:opacity-90"
    >
      {image ? (
        <img
          src={image.url.thumbnail}
          alt={image.description}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
      ) : (
        <ImagePlaceholder className="absolute inset-0 h-full w-full" />
      )}

      <div className="absolute inset-0 bg-linear-to-b from-transparent from-40% to-[rgba(30,30,30,0.78)] to-95%" />

      <div className="absolute top-2 left-2">
        <Discount minPrice={offer.minDiscountPrice} maxPrice={offer.standardPrice} />
      </div>

      <WatchHeartButton
        slug={offer.location.slug}
        unitId={offer.unit.id}
        source="article"
        className="absolute top-1 right-1"
      />

      <div className="absolute inset-x-0 bottom-0 p-3 text-white">
        <div className="truncate text-base font-semibold">{unitFullName(offer.location, offer.unit)}</div>
        <div className="text-xs text-white/70">{offer.location.region.name}</div>
        <div className="mt-1 text-sm font-medium">
          od&nbsp;
          <Price price={offer.minDiscountPrice} />
          &nbsp;/&nbsp;noc
        </div>
      </div>
    </Link>
  )
}
